import React, { Component } from 'react';
import { Button, Progress } from 'antd';
import moment from 'moment';
import { withTranslation } from 'react-i18next';

import { connect } from '../../../StateProvider';
import { IMPORT_STATUS, RECORD_STATUS_FIELD_ID, RECORD_STATUSES } from '../../../../configs/import';
import LoadingSpin from '../../../common/LoadingSpin';

import styles from '../ImportStyles.less';

class ImportFooter extends Component {
  state = {
    successDownload: false,
  };

  format = (percent) => {
    if (!percent) return;
    if (Math.floor(percent) == 100 && !this.state.successDownload) {
      this.setState({ successDownload: true });
      this.props.onChangeFinishState();
    }
    return `${Math.floor(percent)}%`;
  };

  matchFormat = (percent) => (percent ? `${Math.floor(percent)}%` : '');

  render() {
    const {
      importStatus,
      loading,
      recordsCount,
      recordsCountByStatus,
      startSaving,
      stopSaving,
      stopMatch,
      closeModal,
      waitingTime,
      showSpin,
      importing,
      importMode,
      matchSummary,
      matchProgress,
      t,
    } = this.props;

    const mode = importMode || 'create';
    const matching = importStatus === IMPORT_STATUS.MATCHING.STATUS;

    const imported =
      (+recordsCountByStatus.get(RECORD_STATUSES.IMPORT_COMPLETED) || 0) +
      (+recordsCountByStatus.get(RECORD_STATUSES.IMPORT_UPDATED) || 0);
    const failed = +recordsCountByStatus.get(RECORD_STATUSES.IMPORT_FAILED) || 0;
    const parsed =
      (+recordsCountByStatus.get(RECORD_STATUSES.PARSE_COMPLETED) || 0) +
      (+recordsCountByStatus.get(RECORD_STATUSES.MATCH_UPDATE) || 0) +
      (+recordsCountByStatus.get(RECORD_STATUSES.MATCH_CREATE) || 0);

    const parsedRecordsSize = imported + failed + parsed;
    const parsedRecordsPercent = ((imported + failed) / parsedRecordsSize) * 100;
    const importedRecordsPercent = (imported / parsedRecordsSize) * 100;
    const isFinished = imported == parsedRecordsSize;

    let buttonType = 'primary';
    let buttonClassName;
    let buttonOnClick;
    let buttonChildren;
    let disabled = false;

    if (matching) {
      buttonClassName = styles.buttonStop;
      buttonOnClick = stopMatch;
      buttonChildren = t('import.buttons.importStop');
    } else if (importStatus === IMPORT_STATUS.SAVING.STATUS) {
      buttonClassName = styles.buttonStop;
      buttonOnClick = stopSaving;
      disabled = loading;
      buttonChildren = t('import.buttons.importStop');
    } else if (parsed === 0 && importStatus !== IMPORT_STATUS.COMPLETED.STATUS) {
      disabled = true;
      buttonOnClick = startSaving;
      buttonChildren = t('import.buttons.importStart');
    } else if (importStatus === IMPORT_STATUS.COMPLETED.STATUS && isFinished) {
      buttonType = 'defalut';
      buttonOnClick = closeModal;
      buttonChildren = t('import.buttons.importClose');
    } else if (importStatus === IMPORT_STATUS.COMPLETED.STATUS && !isFinished) {
      buttonOnClick = startSaving;
      disabled = loading;
      buttonChildren = t('import.buttons.importStartRest');
    } else if (importStatus === IMPORT_STATUS.STOPED.STATUS) {
      buttonOnClick = startSaving;
      disabled = loading;
      buttonChildren = t('import.buttons.importResume');
    } else {
      buttonOnClick = startSaving;
      disabled = loading;
      buttonChildren = t('import.buttons.importStart');
    }

    const matchTotal = matchProgress ? +matchProgress.get('total') || 0 : 0;
    const matchDone = matchProgress ? +matchProgress.get('done') || 0 : 0;
    const matchPercent = matchTotal ? (matchDone / matchTotal) * 100 : 0;

    let hint = recordsCount ? `${t('import.progress.indicator.all')} ${recordsCount}` : '';
    if (parsedRecordsSize > 0) {
      hint = `${hint} / ${t('import.progress.indicator.parsedRecords')} ${parsedRecordsSize}`;
    }
    if (imported > 0) {
      hint = `${hint} / ${t('import.progress.indicator.imported')} ${imported}`;
    }
    if (failed > 0) {
      hint = `${hint} / ${t('import.progress.indicator.failed')} ${failed}`;
    }

    const matchResult =
      matchSummary &&
      (mode === 'update'
        ? t('import.match.summaryUpdate', {
            update: matchSummary.get('update'),
            skip: matchSummary.get('skip') || 0,
            dup: matchSummary.get('dup'),
          })
        : t('import.match.summaryUpsert', {
            update: matchSummary.get('update'),
            create: matchSummary.get('create'),
            dup: matchSummary.get('dup'),
          }));

    let progressHint = hint;
    if (matching) {
      progressHint = t('import.match.progress', { done: matchDone, total: matchTotal });
    } else if (matchResult) {
      progressHint = matchResult;
    }

    return (
      <div className={styles.footerWrapper}>
        <div className={styles.leftPart}>
          <Button
            key="saveData"
            type={buttonType}
            disabled={disabled}
            className={buttonClassName}
            onClick={buttonOnClick}
          >
            {buttonChildren}
          </Button>

          <div className={styles.progressWrapper}>
            <div className={styles.progressHint}>{progressHint}</div>
            {matching ? (
              <Progress percent={matchPercent} className={styles.progressComponent} format={this.matchFormat} />
            ) : (
              <Progress
                percent={parsedRecordsPercent}
                successPercent={importedRecordsPercent}
                className={styles.progressComponent}
                format={this.format}
              />
            )}
          </div>
        </div>

        <div className={styles.rightPart}>
          {showSpin && <LoadingSpin className={styles.spin} />}
          {waitingTime && importing && (
            <div title={t('import.timer.waitingTime')} className={styles.timerWrapper}>
              {waitingTime}
            </div>
          )}
        </div>
      </div>
    );
  }
}

export default connect(
  withTranslation()(ImportFooter),
  {
    importStatus: ['import', 'importStatus'],
    loading: ['import', 'loading'],
    records: ['import', 'records'],
    waitingTime: ['import', 'waitingTime'],
    importMode: ['import', 'importMode'],
    matchSummary: ['import', 'matchSummary'],
    matchProgress: ['import', 'matchProgress'],
  },
  (props, { records, filteredRecords, waitingTime, ...state }) => {
    const { loading, importStatus } = state;

    const recordsCountByStatus = records && records.countBy((r) => r.getIn(['values', RECORD_STATUS_FIELD_ID, 0]));

    const recordsCount = records && records.size;
    const saving = importStatus === IMPORT_STATUS.LOADING.STATUS;
    const importing = importStatus === IMPORT_STATUS.SAVING.STATUS;
    const matching = importStatus === IMPORT_STATUS.MATCHING.STATUS;
    const showSpin = loading || saving || importing || matching;
    waitingTime = waitingTime && moment.utc(waitingTime).format('HH:mm:ss');

    return {
      ...state,
      ...props,
      recordsCount,
      recordsCountByStatus,
      showSpin,
      waitingTime,
      importing,
    };
  },
);
